import React, { useMemo, useState, useRef } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import Layout from 'components/Common/Layout';
import useTranslation from 'hooks/useTranslation';
import * as api from 'utils/api';
import { getFileUrl } from 'utils/file';
import ReactLoading from 'components/Common/ReactLoading';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import styles from './GenAIImageHistory.module.scss';

const DAYS_PER_PAGE = 10;

export default function GenAIImageHistory() {
  const { tGenAIImageHistory } = useTranslation();
  const history = useHistory();
  const [hoveredImage, setHoveredImage] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0, isBelow: false });
  const popupRef = useRef(null);

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['GenAIImagesHistory'],
    queryFn: ({ pageParam = 0 }) => {
      const startDate = moment()
        .subtract((pageParam + 1) * DAYS_PER_PAGE, 'days')
        .startOf('day');
      const endDate = moment()
        .subtract(pageParam * DAYS_PER_PAGE, 'days')
        .endOf('day');
      return api.getGenAiImages({
        start: startDate.valueOf(),
        end: endDate.valueOf(),
      });
    },
    getNextPageParam: (lastPage, allPages) => {
      // Continue fetching if hasMore is true
      if (lastPage && lastPage.hasMore) {
        return allPages.length;
      }
      return undefined;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Group images by date
  const groupedImages = useMemo(() => {
    if (!data?.pages) return {};
    const groups = {};
    data.pages.forEach(page => {
      if (page && page.images && Array.isArray(page.images)) {
        page.images.forEach(image => {
          const date = new Date(image.createdAt).toDateString();
          if (!groups[date]) {
            groups[date] = [];
          }
          groups[date].push(image);
        });
      }
    });
    return groups;
  }, [data]);

  const handleImageClick = (imageId) => {
    history.push(`/gen-ai-images/${imageId}`);
  };

  const handleImageHover = (imageId, event) => {
    if (!imageId) {
      setHoveredImage(null);
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const popupWidth = 340; // Updated for 2x larger images (320px + padding)
    const popupHeight = 540; // Updated for 2x larger images (2 variants + gaps + padding)

    // Calculate position - default to the right of the image
    let top = rect.top;
    let left = rect.right + 10;
    const isBelow = false;

    // Adjust if popup would go off screen horizontally
    if (left + popupWidth > window.innerWidth - 10) {
      left = rect.left - popupWidth - 10; // Show on left instead
    }

    // Adjust if popup would go off screen vertically
    if (top + popupHeight > window.innerHeight - 10) {
      top = window.innerHeight - popupHeight - 10; // Adjust to fit
    }
    if (top < 10) {
      top = 10; // Keep at least 10px from top
    }

    setPopupPosition({ top, left, isBelow });
    setHoveredImage(imageId);
  };

  const renderImageGrid = (images) => (
    <div className={styles.imageGrid}>
      {images.map((image) => {
        // Get variant with earliest creation date, or use inputFileId if no variants
        let fileId = image.inputFileId; // Default fallback
        let hasVariants = false;

        if (image.variants && image.variants.length > 0) {
          hasVariants = true;
          // Sort variants by createdAt (earliest first) and get the first one
          const sortedVariants = [...image.variants].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
          const earliestVariant = sortedVariants[0];
          fileId = earliestVariant.highResFileId || earliestVariant.lowResFileId || image.inputFileId;
        }

        if (!fileId) return null;

        return (
          <div
            key={image.id}
            className={styles.imageCard}
            onClick={() => handleImageClick(image.id)}
            onMouseEnter={(e) => hasVariants && handleImageHover(image.id, e)}
            onMouseLeave={() => handleImageHover(null)}
            data-image-id={image.id}
          >
            <img
              src={getFileUrl(fileId)}
              alt={`${image.roomType} - ${image.style}`}
              className={styles.image}
              loading="lazy"
            />
            <div className={styles.imageOverlay}>
              <div className={styles.imageInfo}>
                <span className={styles.roomType}>{image.roomType}</span>
                <span className={styles.style}>{image.style}</span>
              </div>
            </div>
          </div>
        );
      })}

      {/* Variant popup overlay */}
      {hoveredImage && (
        <div className={styles.variantPopupOverlay}>
          {(() => {
            // Find the hovered image data
            const hoveredImageData = images.find(img => img.id === hoveredImage);
            if (!hoveredImageData?.variants?.length) return null;

            const sortedVariants = [...hoveredImageData.variants].sort(
              (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
            );
            const variantsToShow = sortedVariants.slice(0, 2).map(variant => ({
              id: variant.id,
              fileId: variant.highResFileId || variant.lowResFileId,
            })).filter(variant => variant.fileId);

            if (variantsToShow.length === 0) return null;

            return (
              <div
                ref={popupRef}
                className={`${styles.variantPopup} ${popupPosition.isBelow ? styles.popupBelow : ''}`}
                style={{
                  top: `${popupPosition.top}px`,
                  left: `${popupPosition.left}px`,
                }}
              >
                <div className={styles.variantGrid}>
                  {variantsToShow.map((variant, index) => (
                    <div key={variant.id} className={styles.variantItem}>
                      <img
                        src={getFileUrl(variant.fileId)}
                        alt={`Variant ${index + 1}`}
                        className={styles.variantImage}
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );

  const formatDateLabel = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return tGenAIImageHistory('today') || 'Today';
    }
    if (date.toDateString() === yesterday.toDateString()) {
      return tGenAIImageHistory('yesterday') || 'Yesterday';
    }
    return moment(date).format('DD/MM/YYYY');
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="container">
          <div className={styles.title}>
            {tGenAIImageHistory('title')}
          </div>
          <div className={styles.loadingContainer}>
            <ReactLoading />
          </div>
        </div>
      </Layout>
    );
  }

  if (isError) {
    return (
      <Layout>
        <div className="container">
          <div className={styles.title}>
            {tGenAIImageHistory('title')}
          </div>
          <div className={styles.errorMessage}>
            {tGenAIImageHistory('errorMessage') || 'Failed to load image history. Please try again.'}
          </div>
        </div>
      </Layout>
    );
  }

  const hasImages = Object.keys(groupedImages).length > 0;

  return (
    <Layout>
      <div className="container">
        <div className={styles.title}>
          {tGenAIImageHistory('title')}
        </div>
        {!hasImages ? (
          <div className={styles.emptyState}>
            <p>{tGenAIImageHistory('emptyMessage') || 'No images found in your generation history.'}</p>
          </div>
        ) : (
          <div className={styles.historyContainer}>
            <div className={styles.timeline}>
              {Object.entries(groupedImages)
                .sort(([dateA], [dateB]) => new Date(dateB) - new Date(dateA))
                .map(([date, images], index, array) => (
                  <div key={date} className={styles.timelineItem}>
                    <div className={styles.timelineDate}>
                      <div className={styles.datePoint}>
                        <div className={styles.orangeDot} />
                        {index < array.length - 1 && (
                          <div className={styles.timelineLine} />
                        )}
                      </div>
                      <div className={styles.dateLabel}>
                        <div className={styles.dateText}>
                          {formatDateLabel(date)}
                        </div>
                        <div className={styles.imageCount}>
                          (
                          {images.length}
                          {' '}
                          {tGenAIImageHistory('imagesText') || 'images'}
                          )
                        </div>
                      </div>
                    </div>
                    <div className={styles.timelineContent}>
                      {renderImageGrid(images)}
                    </div>
                  </div>
                ))}
            </div>
            {hasNextPage && (
              <div className={styles.loadMoreContainer}>
                <button
                  type="button"
                  className={styles.loadMoreButton}
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                >
                  {isFetchingNextPage ? (
                    <>
                      <ReactLoading size={16} />
                      Loading...
                    </>
                  ) : (
                    tGenAIImageHistory('loadMore') || 'Load More'
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
